import { gql } from '@apollo/client';
import { BASE_ROUTE_FIELDS, ROUTE_STAGING_VERIFICATION_FIELDS } from '../../graphql/queries/routes';
import { ORDER_FIELDS } from '../../graphql/queries/orders';
import { STOP_FIELDS } from '../../graphql/queries/stops';
import { ITEM_FIELDS } from '../../graphql/queries/items';
import { EXCEPTION_FIELDS } from '@/graphql/queries/db_exceptions';
import { ORDER_ROUTE_MAPPINGS } from '@/graphql/queries/order_route_mappings';
import { RATING_FIELDS } from '@/graphql/queries/ratings';
import { WAREHOUSE_EVENT_FIELDS } from '@/graphql/queries/order_wh_events';
import { TRACKING_EVENT_FIELDS } from '@/graphql/fragments/tracking_events';

// Refetched on ROUTE_CHANGE_SUBSCRIPTION rather than re-sent ~1s. Selection set deliberately
// unchanged, so no consumer can break on a missing field.
export const ROUTE_QUERY = gql`
    ${BASE_ROUTE_FIELDS}
    ${ORDER_FIELDS}
    ${STOP_FIELDS}
    ${ITEM_FIELDS}
    ${EXCEPTION_FIELDS}
    ${ORDER_ROUTE_MAPPINGS}
    ${RATING_FIELDS}
    ${ROUTE_STAGING_VERIFICATION_FIELDS}
    query RouteDetailsRoute($route_id: uuid = "") {
        routes_by_pk(route_id: $route_id) {
            # Not RouteFields: it spreads ClientFields into both route clients, and only
            # business_name renders.
            ...BaseRouteFields
            ...RouteStagingVerificationFields
            route_shipper {
                client_id
                business_name
            }
            route_carrier {
                client_id
                business_name
            }
            stopsByRouteId {
                ...StopFields
            }
            orders {
                ...OrderRouteMappings
                order {
                    ...OrderFields
                    itemsByOrderId {
                        ...ItemFields
                    }
                    services {
                        service_id
                        service_time
                        quantity
                    }
                    # Only client fields reached off these orders: business_name (OrderInfo),
                    # special_instructions_settings (buildReturnClone), and user.circles
                    # (orderIsExternal — skip-comingled orders are completed in the shipper's
                    # own app, so stop completion emits no tracking_events for them). client_id
                    # keys the Apollo cache — dropping it denormalizes.
                    order_shipper {
                        client_id
                        business_name
                        special_instructions_settings
                        user {
                            # user_id keys the users cache. Without it InMemoryCache throws while
                            # extracting keyFields and the whole route write is discarded.
                            user_id
                            circles
                        }
                    }
                    order_carrier {
                        client_id
                        business_name
                    }
                    ratings {
                        ...RatingFields
                    }
                }
            }
            teammateByDriverId {
                teammate_id
                username
                phone
            }
        }
    }
`;

// Re-run ~1s per viewer, so ids and timestamps only.
//
// Children are listed individually because orders.last_modified_at does not cover them: the only
// child-to-parent path, orders_recompute_readonly_wh_dates(), UPDATEs on nine wh date fields only.
// Aggregates would be cheaper, but allow_aggregations is off for order_wh_events, exceptions and
// stops on the app's roles.
export const ROUTE_CHANGE_SUBSCRIPTION = gql`
    subscription SubscribeToRouteChanges($route_id: uuid = "") {
        routes_by_pk(route_id: $route_id) {
            route_id
            last_modified_at
            stopsByRouteId {
                stop_id
                last_modified_at
            }
            orders {
                # No last_modified_at on this table, and INSERT_EXCEPTION_EVENT updates
                # duplicate_override in place.
                mapping_id
                type
                duplicate_override
                order {
                    order_id
                    last_modified_at
                    wh_events {
                        event_id
                        last_modified_at
                    }
                    # Keyed on mapping_id: one exception fans out to many mapping rows, 36 at most.
                    # status/resolution are compared by value, which is all that works until
                    # public.exceptions gains a last_modified_at trigger; the timestamp then covers
                    # every other edit. Both, so neither repo has to ship first.
                    exceptions {
                        mapping_id
                        last_modified_at
                        exception {
                            last_modified_at
                            status
                            resolution
                        }
                    }
                    itemsByOrderId {
                        item_id
                        last_modified_at
                    }
                    ratings {
                        rating_id
                        last_modified_at
                    }
                }
            }
        }
    }
`;

// Apollo caches subscription results, and this document's two-field `exceptions` would replace
// the rich list ROUTE_QUERY wrote: exception_mappings has no typePolicy, so it is not normalized.
export const ROUTE_CHANGE_SUBSCRIPTION_OPTIONS = { fetchPolicy: 'no-cache' };

export const ROUTE_QUERY_OPTIONS = {
    // The change feed cannot guarantee freshness on mount.
    fetchPolicy: 'cache-and-network',
    // Floor on staleness, not the refresh mechanism. graphql-ws gives up after 5 reconnect
    // attempts, and a GraphQL error ends the subscription outright. Also covers what the signal
    // never watches: documents, notes, tags, references, both rate overrides. 20.6 ms a minute.
    pollInterval: 60000,
};

export const START_ROUTE = gql`
    mutation RouteDetailsStartRoute($route_id: uuid!, $_set: routes_set_input = {}) {
        update_routes_by_pk(pk_columns: { route_id: $route_id }, _set: $_set) {
            route_id
            status
            route_number
        }
    }
`;

export const UPDATE_STOP_EVENTS_MANY = gql`
    ${WAREHOUSE_EVENT_FIELDS}
    ${ORDER_FIELDS}
    ${STOP_FIELDS}
    ${TRACKING_EVENT_FIELDS}

    mutation UpdateStopEvents(
        $routeUpdates: [routes_updates!] = {}
        $orderUpdates: [orders_updates!] = {}
        $stopUpdates: [stops_updates!] = {}
        $eventUpdates: [order_wh_events_updates!] = {}
        $insertEvents: [order_wh_events_insert_input!] = {}
        $trackingEvents: [tracking_events_insert_input!] = []
    ) {
        update_routes_many(updates: $routeUpdates) {
            affected_rows
            returning {
                route_id
                status
                route_number
            }
        }
        update_orders_many(updates: $orderUpdates) {
            affected_rows
            returning {
                ...OrderFields
            }
        }
        update_stops_many(updates: $stopUpdates) {
            affected_rows
            returning {
                ...StopFields
            }
        }
        update_order_wh_events_many(updates: $eventUpdates) {
            affected_rows
            returning {
                ...WarehouseEvents
            }
        }
        insert_order_wh_events(objects: $insertEvents) {
            affected_rows
            returning {
                ...WarehouseEvents
            }
        }

        # Per-item lifecycle rows. on_conflict makes a double-submit idempotent: the constraint is
        # (order_id, item_id, event_type, event_time), so an identical re-send collapses while a
        # genuinely later event at a new timestamp still inserts.
        insert_tracking_events(
            objects: $trackingEvents
            on_conflict: { constraint: tracking_events_uniq, update_columns: [] }
        ) {
            affected_rows
            returning {
                ...TrackingEventFields
            }
        }
    }
`;

export const COMPLETE_ROUTE = gql`
    mutation RouteDetailsCompleteRoute($routeUpdates: [routes_updates!] = {}) {
        update_routes_many(updates: $routeUpdates) {
            affected_rows
            returning {
                route_id
                status
                route_number
            }
        }
    }
`;
